import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
} from '@mui/material';
import type { SlackChannel } from '../../../types/slack.types';

interface SlackChannelMappingProps {
  notificationMappings: Record<string, string>;
  channels: SlackChannel[];
  channelsLoading: boolean;
  onChange: (mappings: Record<string, string>) => void;
}

const NOTIFICATION_TYPES = [
  { key: 'LESSON_RESCHEDULED', label: 'Lesson Rescheduled' },
  { key: 'LESSON_CANCELLED', label: 'Lesson Cancelled' },
  { key: 'ABSENCE_CONFIRMED', label: 'Absence Reports' },
  { key: 'MEET_AND_GREET_BOOKING', label: 'Meet & Greet Bookings' },
  { key: 'PAYMENT_RECEIVED', label: 'Payment Received' },
  { key: 'INVOICE_CREATED', label: 'Invoice Created' },
  { key: 'HYBRID_BOOKING_OPENED', label: 'Hybrid Booking Opened' },
  { key: 'MESSAGE_RECEIVED', label: 'Message Forwarding' },
  { key: 'SUBSCRIPTION_PAYMENT_FAILED', label: 'Payment Failed' },
];

export default function SlackChannelMapping({
  notificationMappings,
  channels,
  channelsLoading,
  onChange,
}: SlackChannelMappingProps) {
  const handleChannelChange = (notificationType: string, channelId: string) => {
    const updated = { ...notificationMappings };
    if (channelId === '') {
      delete updated[notificationType];
    } else {
      updated[notificationType] = channelId;
    }
    onChange(updated);
  };

  if (channelsLoading) {
    return <Skeleton variant="rectangular" height={300} />;
  }

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        Map each notification type to a specific Slack channel. Unmapped types
        use the default channel.
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Notification Type</TableCell>
              <TableCell>Slack Channel</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {NOTIFICATION_TYPES.map((type) => (
              <TableRow key={type.key}>
                <TableCell>{type.label}</TableCell>
                <TableCell>
                  <FormControl size="small" fullWidth>
                    <InputLabel id={`slack-channel-${type.key}-label`} shrink>
                      {type.label}
                    </InputLabel>
                    <Select
                      labelId={`slack-channel-${type.key}-label`}
                      value={notificationMappings[type.key] || ''}
                      onChange={(e) =>
                        handleChannelChange(type.key, e.target.value)
                      }
                      displayEmpty
                      label={type.label}
                    >
                      <MenuItem value="">
                        <em>Default channel</em>
                      </MenuItem>
                      {channels.map((ch) => (
                        <MenuItem key={ch.id} value={ch.id}>
                          #{ch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
